import useTranslation from "../../hooks/useTranslation";
import { GetDemographicGroupsResponse } from "../../types/workplaceCulture";
import { getOrganizationDemographic } from "../../utils/WorkplaceCulture.utils";
import { Paper } from "../ui/Paper/Paper";
import { Typography } from "../ui/Typography/Typography";
import {
  getMajorityMinorityData,
  getMajorityMinorityDescriptionKey,
} from "./ReportMajorityMinority.utils";
import { ReportMajorityMinorityCard } from "./ReportMajorityMinorityCard";
import "./ReportMajorityMinority.scss";

interface ReportMajorityMinorityProps {
  demographicsData?: GetDemographicGroupsResponse;
}

export const ReportMajorityMinority = ({
  demographicsData,
}: ReportMajorityMinorityProps) => {
  const { t } = useTranslation("reportPage");

  const data = getMajorityMinorityData(demographicsData);
  const orgDemographic = getOrganizationDemographic(demographicsData);

  if (!data || !orgDemographic?.score) {
    return (
      <Paper shadow className="ReportMajorityMinority">
        <Typography
          tagVariant="h2"
          desktop="h2"
          className="ReportMajorityMinority__title"
          color="darkerStoneBlue"
        >
          {t("majorityMinorityGroups.title")}
        </Typography>
        <div className="ReportMajorityMinority__noData">
          <Typography
            desktop="body1"
            fontStyle="italic"
            color="darkerStoneBlue"
          >
            {t("majorityMinorityGroups.noData")}
          </Typography>
        </div>
      </Paper>
    );
  }

  const {
    majorityScore,
    minorityScore,
    majorityCount,
    minorityCount,
    majorityUnderThreshold,
    minorityUnderThreshold,
  } = data;

  const { openingKey, comparisonKey } = getMajorityMinorityDescriptionKey(
    orgDemographic.score,
    majorityScore,
    minorityScore
  );

  return (
    <Paper shadow className="ReportMajorityMinority">
      <div className="ReportMajorityMinority__content">
        <div className="ReportMajorityMinority__content__description">
          <Typography
            tagVariant="h2"
            desktop="h2"
            className="ReportMajorityMinority__title"
            color="darkerStoneBlue"
          >
            {t("majorityMinorityGroups.title")}
          </Typography>
          <Typography desktop="body1" color="secondary">
            {t(`majorityMinorityGroups.description.${openingKey}`)}{" "}
            {t(`majorityMinorityGroups.description.${comparisonKey}`)}
          </Typography>
        </div>

        <div className="ReportMajorityMinority__content__cards">
          <ReportMajorityMinorityCard
            groupLabelKey="majority"
            score={majorityScore}
            count={majorityCount}
            isUnderThreshold={majorityUnderThreshold}
            t={t}
          />

          <ReportMajorityMinorityCard
            groupLabelKey="minority"
            score={minorityScore}
            count={minorityCount}
            isUnderThreshold={minorityUnderThreshold}
            t={t}
          />
        </div>
      </div>
    </Paper>
  );
};
